import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import { safeNext } from "../utils/authRedirect";

declare const process: {
  env: {
    REACT_APP_COGNITO_USER_POOL_ID?: string;
    REACT_APP_COGNITO_CLIENT_ID?: string;
    REACT_APP_COGNITO_DOMAIN?: string;
  };
};

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || "us-east-1_bgKigZxAZ",
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || "14vtbeovo29i5nlin545uumhmv",
});

/**
 * `userConfirmed` is false once the pool stops auto-confirming (deploy repo,
 * RequireEmailVerification=true): Cognito has emailed a code and the account
 * cannot sign in until `confirmSignUp` succeeds.
 *
 * `turnstileToken` travels as SignUp ValidationData, which only the pre-signup
 * Lambda sees — the server-side check that a direct API caller can't skip.
 */
export function signUp(
  email: string,
  password: string,
  turnstileToken?: string
): Promise<{ userConfirmed: boolean }> {
  const attributes = [
    new CognitoUserAttribute({ Name: "email", Value: email }),
  ];
  const validationData = turnstileToken
    ? [new CognitoUserAttribute({ Name: "turnstileToken", Value: turnstileToken })]
    : [];

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, attributes, validationData, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve({ userConfirmed: result?.userConfirmed ?? false });
    });
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });

  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err: Error | undefined) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });

  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err: Error | undefined) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

export function signIn(
  username: string,
  password: string
): Promise<{ idToken: string; accessToken: string }> {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  const authDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
        });
      },
      onFailure: (err: Error) => {
        reject(err);
      },
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });

  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}

export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });

  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}

/**
 * Local-only on purpose: this clears the cached tokens but does NOT send the
 * browser through Cognito's /logout endpoint. Trade-off: the hosted-UI cookie
 * and the person's Google session survive, so the next "Continue with Google"
 * may skip the account chooser and sign straight back in as the same account.
 */
export function signOut(): void {
  const user = userPool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

export function getCurrentSession(): Promise<{
  username: string;
  email: string;
  idToken: string;
  accessToken: string;
  groups: string[];
} | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }

    user.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        const idToken = session.getIdToken();
        const payload = idToken.decodePayload();
        const accessPayload = session.getAccessToken().decodePayload();

        resolve({
          username:
            payload["cognito:username"] || payload["preferred_username"] || "",
          email: (payload["email"] as string) || "",
          idToken: idToken.getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          groups: (accessPayload["cognito:groups"] as string[] | undefined) ?? [],
        });
      }
    );
  });
}

// ---------------------------------------------------------------------------
// "Continue with Google": Cognito hosted-UI authorization-code flow with PKCE.
//
// The hosted UI is only the transport to Google. Once the code is exchanged,
// the tokens are installed as an ordinary amazon-cognito-identity-js session,
// so getCurrentSession(), refresh, requests.ts and signOut() need no changes.
// ---------------------------------------------------------------------------

const GOOGLE_IDENTITY_PROVIDER = "Google";
const GOOGLE_SCOPES = "openid email profile aws.cognito.signin.user.admin";
const GOOGLE_CALLBACK_PATH = "/auth/callback";

/** verifier + state + destination of the sign-in this tab started. */
const PENDING_KEY = "dejachart.google.pending";
/** Set while the one automatic post-linking restart is in flight. */
const RETRY_KEY = "dejachart.google.retried";

/** Cognito's first-sign-in-after-account-linking error (see retryGoogleSignIn). */
const LINKING_ERROR = /already found an entry for username/i;

/**
 * Hosted-UI host (no scheme), or "" when the feature is off. Read per call,
 * not at import, so tests can toggle it; CRA still inlines the env literal.
 */
export function googleSignInDomain(): string {
  const raw = (process.env.REACT_APP_COGNITO_DOMAIN || "").trim();
  return raw.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
}

export function isGoogleSignInEnabled(): boolean {
  return googleSignInDomain() !== "";
}

export type GoogleSignInErrorKind =
  | "provider" // Cognito/Google redirected back with `error` (e.g. access_denied)
  | "linking" // the known first-sign-in-after-linking error; restart fixes it
  | "state" // no sign-in in progress, missing code, or `state` mismatch (CSRF guard)
  | "exchange" // the token endpoint refused the code
  | "session"; // tokens came back but no usable session could be built

export class GoogleSignInError extends Error {
  readonly kind: GoogleSignInErrorKind;
  /** OAuth `error` code from the redirect, e.g. "access_denied". */
  readonly code?: string;
  /** Destination the sign-in was started for, so a retry or a link back to
   *  /login can still return the visitor there. */
  readonly next: string | null;

  constructor(
    kind: GoogleSignInErrorKind,
    message: string,
    options: { code?: string; next?: string | null } = {}
  ) {
    super(message);
    this.name = "GoogleSignInError";
    this.kind = kind;
    this.code = options.code;
    this.next = options.next ?? null;
  }
}

export function isGoogleSignInError(error: unknown): error is GoogleSignInError {
  return error instanceof GoogleSignInError;
}

interface PendingSignIn {
  verifier: string;
  state: string;
  next: string | null;
}

function readPending(): PendingSignIn | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingSignIn>;
    if (typeof parsed.verifier !== "string" || typeof parsed.state !== "string") return null;
    return {
      verifier: parsed.verifier,
      state: parsed.state,
      next: typeof parsed.next === "string" ? parsed.next : null,
    };
  } catch {
    return null;
  }
}

function clearPending(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // storage blocked: nothing was stored either
  }
}

function setRetryFlag(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(RETRY_KEY, "1");
    else sessionStorage.removeItem(RETRY_KEY);
  } catch {
    // storage blocked: startGoogleSignIn would already have failed
  }
}

function retryFlagSet(): boolean {
  try {
    return sessionStorage.getItem(RETRY_KEY) === "1";
  } catch {
    return false;
  }
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomToken(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

/** PKCE S256: base64url(SHA-256(verifier)). The verifier is base64url, so ASCII. */
async function s256Challenge(verifier: string): Promise<string> {
  const data = new Uint8Array(verifier.length);
  for (let i = 0; i < verifier.length; i++) data[i] = verifier.charCodeAt(i);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64Url(new Uint8Array(digest));
}

function googleRedirectUri(): string {
  return `${window.location.origin}${GOOGLE_CALLBACK_PATH}`;
}

async function beginGoogleSignIn(next?: string | null): Promise<void> {
  const domain = googleSignInDomain();
  if (!domain) throw new Error("Google sign-in is not configured.");

  const verifier = randomToken(32); // 43 chars, inside RFC 7636's 43-128
  const state = randomToken(16);
  const challenge = await s256Challenge(verifier);

  // `next` rides in sessionStorage rather than the OAuth `state`, which stays an
  // opaque nonce. It is re-sanitized on the way out as well as here.
  const pending: PendingSignIn = { verifier, state, next: safeNext(next) };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));

  const query = new URLSearchParams({
    identity_provider: GOOGLE_IDENTITY_PROVIDER,
    response_type: "code",
    client_id: userPool.getClientId(),
    redirect_uri: googleRedirectUri(),
    scope: GOOGLE_SCOPES,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.assign(`https://${domain}/oauth2/authorize?${query.toString()}`);
}

/** Send the browser to Google (via the hosted UI). `next` is where to land afterwards. */
export async function startGoogleSignIn(next?: string): Promise<void> {
  setRetryFlag(false); // a person-initiated attempt gets its own automatic retry
  await beginGoogleSignIn(next);
}

/**
 * Restart sign-in once after a "linking" error. Cognito raises "Already found an
 * entry for username Google_…" on the first Google sign-in after the pre-signup
 * Lambda links Google to an existing email account; the link is in place by
 * then, so a second attempt succeeds. Resolves false (and resets the guard)
 * when this tab already retried, so a persistent failure can't loop.
 */
export async function retryGoogleSignIn(next?: string | null): Promise<boolean> {
  if (retryFlagSet()) {
    setRetryFlag(false);
    return false;
  }
  setRetryFlag(true);
  await beginGoogleSignIn(next);
  return true;
}

interface OAuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

async function exchangeGoogleCode(
  code: string,
  verifier: string,
  next: string | null
): Promise<OAuthTokens> {
  const failure = (oauthCode?: string): GoogleSignInError =>
    new GoogleSignInError("exchange", "Google sign-in could not be completed.", {
      code: oauthCode,
      next,
    });

  let response: Response;
  try {
    response = await fetch(`https://${googleSignInDomain()}/oauth2/token`, {
      method: "POST",
      // A "simple" content type: no CORS preflight, and no client secret to send.
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: userPool.getClientId(),
        code,
        redirect_uri: googleRedirectUri(),
        code_verifier: verifier,
      }).toString(),
    });
  } catch {
    throw failure();
  }

  let body: { id_token?: string; access_token?: string; refresh_token?: string; error?: string } = {};
  try {
    body = await response.json();
  } catch {
    // non-JSON error page: fall through to the generic failure
  }
  if (!response.ok || !body.id_token || !body.access_token || !body.refresh_token) {
    throw failure(body.error);
  }
  // A missing refresh token is a hard failure, not a degraded session: the
  // library cannot cache a session without one, and an hour-long login that
  // silently lapses is a worse bug to chase than a failed sign-in.
  return {
    idToken: body.id_token,
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
  };
}

/**
 * Install the tokens exactly as authenticateUser() would: setSignInUserSession()
 * writes the library's localStorage keys (id/access/refresh token, clockDrift and
 * LastAuthUser), which is everything getCurrentUser()/getSession() read back.
 */
function establishSession(tokens: OAuthTokens, next: string | null): void {
  const idToken = new CognitoIdToken({ IdToken: tokens.idToken });
  const accessToken = new CognitoAccessToken({ AccessToken: tokens.accessToken });
  const username = idToken.decodePayload()["cognito:username"] ?? accessToken.decodePayload()["username"];
  const session = new CognitoUserSession({
    IdToken: idToken,
    AccessToken: accessToken,
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken }),
  });
  if (typeof username !== "string" || !username || !session.isValid()) {
    throw new GoogleSignInError("session", "Google sign-in returned an unusable session.", { next });
  }

  // Drop whichever account was cached before, or its tokens would linger in
  // localStorage once LastAuthUser points at this one.
  userPool.getCurrentUser()?.signOut();
  new CognitoUser({ Username: username, Pool: userPool }).setSignInUserSession(session);
}

/**
 * Handle the /auth/callback redirect: validate it, exchange the code, and
 * install the session. Always consumes the stored PKCE/state entry. Throws a
 * GoogleSignInError for every failure; resolves with the (sanitized) destination.
 */
export async function completeGoogleSignIn(
  params: URLSearchParams
): Promise<{ next: string | null }> {
  const pending = readPending();
  const next = safeNext(pending?.next);
  clearPending();

  try {
    const oauthError = params.get("error");
    const returnedState = params.get("state");
    // An error redirect may omit `state`, but one that carries a state must match.
    const stateOk = !!pending && returnedState === pending.state;

    if (oauthError) {
      if (returnedState !== null && !stateOk) {
        throw new GoogleSignInError("state", "Sign-in state did not match.");
      }
      const description = params.get("error_description") ?? "";
      if (pending && LINKING_ERROR.test(description)) {
        throw new GoogleSignInError("linking", "Account linking needs one more attempt.", {
          code: oauthError,
          next,
        });
      }
      throw new GoogleSignInError("provider", "Google sign-in did not complete.", {
        code: oauthError,
        next,
      });
    }

    const code = params.get("code");
    if (!pending || !stateOk || !code) {
      throw new GoogleSignInError("state", "Sign-in state did not match.");
    }

    establishSession(await exchangeGoogleCode(code, pending.verifier, next), next);
    setRetryFlag(false);
    return { next };
  } catch (error) {
    if (!(isGoogleSignInError(error) && error.kind === "linking")) setRetryFlag(false);
    throw error;
  }
}
